import { useLayoutEffect, useRef } from "react"

/**
 * Syncs the heights of paired elements identified by data-size attributes.
 * For each pair, both elements get the min-height of the taller one.
 *
 * Uses useLayoutEffect so the initial sync runs before the browser paints,
 * eliminating any visible flash of unsync'd heights.
 *
 * @param {Array<[string, string]>} pairs - Array of [sizeA, sizeB] data-size attribute values
 * @param {boolean} active - Whether syncing is active
 */
export function useSyncedHeights(pairs, active) {
  const pairsRef = useRef(pairs)
  pairsRef.current = pairs

  useLayoutEffect(() => {
    if (!active) return

    let observer = null
    let rafId = null
    let updating = false

    function syncHeights() {
      const entries = []

      // Phase 1: Reset all min-heights
      for (const [sizeA, sizeB] of pairsRef.current) {
        const elA = document.querySelector(`[data-size="${sizeA}"]`)
        const elB = document.querySelector(`[data-size="${sizeB}"]`)
        if (!elA || !elB) continue
        elA.style.minHeight = ""
        elB.style.minHeight = ""
        entries.push({ elA, elB })
      }

      // Phase 2: Read natural heights (single reflow)
      const measurements = entries.map(({ elA, elB }) => ({
        elA,
        elB,
        maxHeight: Math.max(elA.offsetHeight, elB.offsetHeight),
      }))

      // Phase 3: Apply min-heights
      for (const { elA, elB, maxHeight } of measurements) {
        elA.style.minHeight = `${maxHeight}px`
        elB.style.minHeight = `${maxHeight}px`
      }
    }

    function handleResize() {
      if (updating) return
      updating = true
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        syncHeights()
        // Suppress observer callbacks from our own changes for one frame
        requestAnimationFrame(() => {
          updating = false
        })
      })
    }

    // Initial sync runs before paint — no flash
    syncHeights()

    observer = new ResizeObserver(handleResize)

    for (const [sizeA, sizeB] of pairsRef.current) {
      const elA = document.querySelector(`[data-size="${sizeA}"]`)
      const elB = document.querySelector(`[data-size="${sizeB}"]`)
      if (elA) {
        observer.observe(elA)
        // Also observe direct children — they don't have min-height set, so they
        // shrink freely when content is removed. Without this, the parent's
        // min-height holds its rendered height steady and ResizeObserver never fires.
        for (const child of elA.children) observer.observe(child)
      }
      if (elB) {
        observer.observe(elB)
        for (const child of elB.children) observer.observe(child)
      }
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      observer?.disconnect()

      for (const [sizeA, sizeB] of pairsRef.current) {
        const elA = document.querySelector(`[data-size="${sizeA}"]`)
        const elB = document.querySelector(`[data-size="${sizeB}"]`)
        if (elA) elA.style.minHeight = ""
        if (elB) elB.style.minHeight = ""
      }
    }
  }, [active])
}
